// EX-headerStore

import * as Links from '#shared/constants/Links';
import * as AccountLinks from '#shared/constants/AccountLinks';
import { defineStore } from 'pinia';
import { useNavUser } from './navUser';
import { useNavViewType } from './navViewType';
import { viewTypes, viewTypeToDeviceType } from '#shared/constants';
import useExperimentsIds from '~/composables/useExperimentsIds';
import { useHeaderFetch } from '~/composables/dumb/useNavigationFetch';
import { loadHeaderMock, normalizeHeaderData, getExperimentIdForMock } from '#shared/utils/headerHelpers';
import {
  GET_HEADER_DATA,
  GET_META,
  GET_SECONDARY_NAV,
  GET_MYACCOUNT_LINKS,
  GET_DEALS_DATA,
  GET_SEARCH_MODAL_STATE,
  GET_ATTRS,
} from '#shared/types/getters';
import {
  SET_FETCH_ERROR,
  SET_HEADER_DATA,
  SET_SECONDARY_NAVIGATION,
  SET_TOP_NAVIGATION,
  SET_ACCOUNT_LINKS,
  SET_SEARCH_MODAL_STATE,
  SET_ACTIVE_SEARCH_TRIGGER_ID,
  SET_ATTRS,
  SET_SEARCH_VALUE,
  SET_SKIP_CONTENT_OFFSET,
  DELETE_HAMBURGER_MENU,
  DELETE_DESKTOP_MENU,
  APPLY_SEARCH_TERM,
} from '#shared/types/actions';

export const useNavHeader = defineStore('navHeader', {
  state: () => ({
    data: {
      [viewTypes.desktop]: null,
      [viewTypes.mobile]: null,
    },
    isFetchError: false,
    attrs: {},
    isSearchModalActive: false,
    permanentSigninTooltip: false,
    activeSearchTriggerId: '',
    searchValue: '',
    skipToContentOffset: 0,
  }),
  getters: {
    [GET_HEADER_DATA]: (state) => {
      const navViewTypeStore = useNavViewType();
      return state.data[navViewTypeStore.viewType];
    },
    getHamburgerMenu() {
      const data = this[GET_HEADER_DATA];
      return data?.menu || {};
    },
    getFlyoutsMenu() {
      const data = this[GET_HEADER_DATA];
      return data?.desktopMenu || [];
    },
    [GET_META]() {
      const data = this[GET_HEADER_DATA];

      return data?.meta || {};
    },
    [GET_SECONDARY_NAV]() {
      const data = this[GET_HEADER_DATA];
      return data?.secondaryNavigation || [];
    },
    [GET_MYACCOUNT_LINKS]() {
      const data = this[GET_HEADER_DATA];
      return this.addSignedInState(data?.accountLinks || []);
    },
    [GET_DEALS_DATA]() {
      const data = this[GET_HEADER_DATA];
      return data?.skinnyDealsBanner;
    },
    [GET_SEARCH_MODAL_STATE](state) {
      return state.isSearchModalActive;
    },
    [GET_ATTRS](state) {
      return state.attrs;
    },
  },
  actions: {
    async processFetchedData(result, isFetchSucceed, viewType, experimentIdsForRequest = []) {
      const pageData = usePageData();
      const envProps = useEnvProps();
      const navKillswitch = useNavKillswitch();
      const { isMcom } = envProps;
      const {
        navigation: { context },
        killswitches = {},
      } = pageData;
      const { isInternational, isDomestic } = context;
      const isMobile = viewType === viewTypes.mobile;
      let data;

      if (isFetchSucceed) {
        data = result;
        this[SET_FETCH_ERROR](false);
      } else {
        const experimentId = getExperimentIdForMock({
          experimentIds: experimentIdsForRequest,
          viewType,
          isInternational,
        });
        data = await loadHeaderMock({
          isInternational,
          isMcom,
          isMobile,
          experimentId,
        });
        this[SET_FETCH_ERROR](true);
        envProps.isNavMockHeader = true;
      }

      const { data: transformedData, filteredKillswitches } = normalizeHeaderData({
        source: data,
        viewType,
        experimentIds: experimentIdsForRequest,
        isDomestic: isDomestic,
        killswitchesOverride: killswitches.override,
        isMcom,
      });

      navKillswitch.applyKillswitches(filteredKillswitches);
      this[SET_HEADER_DATA]({ viewType, data: transformedData });

      return transformedData;
    },
    async fetch(force: boolean, additionalExperiments = []) {
      const { viewType } = useNavViewType();
      const pageData = usePageData();
      const envProps = useEnvProps();
      const { isMcom } = envProps;
      const experimentIds = useExperimentsIds();
      const data = this[GET_HEADER_DATA];

      if (!force && data) {
        return;
      }

      const { context, queryParams: serverQueryParams = {} } = pageData.navigation;
      const { navMock: isMockMode } = serverQueryParams;
      const experimentIdsForRequest = Array.from(new Set([...experimentIds.value, ...additionalExperiments]));

      if (isMockMode) {
        return this.processFetchedData(null, false, viewType, experimentIdsForRequest);
      }

      const { hostlink: clientBaseUrl } = envProps;
      const { regionCode, countryCode } = context;
      const deviceType = viewTypeToDeviceType[viewType];

      try {
        const result = await useHeaderFetch({
          experimentIds: experimentIdsForRequest,
          overrideCMParams: false,
          regionCode,
          countryCode,
          deviceType,
          clientBaseUrl,
          isMcom,
        });
        const isFetchSucceed = result && typeof result === 'object';
        return this.processFetchedData(result, isFetchSucceed, viewType, experimentIdsForRequest);
      } catch (error) {
        console.error('useHeaderFetch error', error);
        return this.processFetchedData(null, false, viewType, experimentIdsForRequest);
      }
    },
    addSignedInState(data) {
      const navUser = useNavUser();
      const { isSignedIn } = navUser?.data || {};

      if (isSignedIn) {
        const signOutLink = {
          text: AccountLinks.default.SIGNOUT_TEXT,
          burstCache: true,
          isSignOut: true,
          ...Links.default.account.signOut,
        };
        return [...data, signOutLink];
      }

      const signInLink = {
        text: AccountLinks.default.SIGNIN_TEXT,
        burstCache: true,
        isSignIn: true,
        ...Links.default.account.signIn,
      };

      return [signInLink, ...data];
    },

    [SET_HEADER_DATA]({ viewType, data }) {
      const currentData = this.data[viewType] || {};

      this.data[viewType] = {
        ...currentData,
        ...data,
        // merge meta and properties with new data instead of replacing it with new data
        meta: {
          ...(currentData.meta || {}),
          ...data.meta,
          properties: {
            ...(currentData.meta?.properties || {}),
            ...data.meta.properties,
          },
        },
        menu: data.menu || currentData.menu,
        desktopMenu: data.desktopMenu || currentData.desktopMenu,
      };
    },
    [SET_SECONDARY_NAVIGATION]({ data, viewType }) {
      this.data[viewType].secondaryNavigation = data;
    },
    [SET_TOP_NAVIGATION]({ data, viewType }) {
      this.data[viewType].topNavigation = data;
    },
    [SET_ACCOUNT_LINKS]({ data, viewType }) {
      this.data[viewType].accountLinks = data;
    },
    [SET_SEARCH_MODAL_STATE](payload) {
      this.isSearchModalActive = payload;
    },
    [SET_ACTIVE_SEARCH_TRIGGER_ID](payload: string | null) {
      this.activeSearchTriggerId = payload;
    },
    [SET_ATTRS](payload) {
      this.attrs = payload;
    },
    [SET_SEARCH_VALUE](payload) {
      this.searchValue = payload;
    },
    [SET_SKIP_CONTENT_OFFSET](payload) {
      this.skipToContentOffset = payload;
    },
    [SET_FETCH_ERROR](flag) {
      this.isFetchError = flag;
    },
    [DELETE_HAMBURGER_MENU](viewType) {
      delete this.data[viewType].menu;
    },
    [DELETE_DESKTOP_MENU](viewType) {
      delete this.data[viewType].desktopMenu;
    },

    [APPLY_SEARCH_TERM]() {
      const { originalSearchTerm, searchTerm, navigationType } = window.__PLP_SEARCH_CONTEXT__ || {};
      let committedSearchTerm = originalSearchTerm || searchTerm;

      if (navigationType === 'BROWSE' && committedSearchTerm) {
        committedSearchTerm = committedSearchTerm.replace(/-/g, ' ');
      }

      if (committedSearchTerm) {
        this[SET_SEARCH_VALUE](committedSearchTerm);
      }
    },
  },
});
